import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { displayName } from "../lib/anagConfig";
import { Loader2, Search, X } from "lucide-react";

// Selezione multipla da anagrafica (default: compagnie assicurative).
// Ordinamento alfabetico + ricerca in tempo reale.
export default function CompagnieMultiSelect({ value = [], onChange, testId, source = "compagnie", emptyLabel = "Nessuna compagnia in anagrafica." }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  useEffect(() => {
    setLoading(true);
    api.get(`/anagrafiche/${source}`).then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [source]);

  const names = useMemo(() => {
    const list = items.map(displayName).filter(Boolean);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b, "it", { sensitivity: "base" }));
  }, [items]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? names.filter((n) => n.toLowerCase().includes(s)) : names;
  }, [names, q]);

  const toggle = (n) => {
    const set = new Set(value || []);
    set.has(n) ? set.delete(n) : set.add(n);
    onChange([...set]);
  };

  const remove = (n) => onChange((value || []).filter((x) => x !== n));

  if (loading) return <div className="mt-1.5 py-2"><Loader2 className="h-4 w-4 animate-spin text-amber-500" /></div>;
  return (
    <div className="mt-1.5" data-testid={testId}>
      {(value || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid={`${testId}-badges`}>
          {(value || []).map((n) => (
            <span key={n} data-testid={`${testId}-badge-${n}`}
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-500 text-[#333] text-xs pl-2.5 pr-1 py-0.5">
              {n}
              <button type="button" onClick={() => remove(n)} data-testid={`${testId}-badge-remove-${n}`}
                className="rounded-full hover:bg-amber-500/30 p-0.5 text-[#333] hover:text-black transition-colors" aria-label={`Rimuovi ${n}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative mb-1.5">
        <Search className="h-3.5 w-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" data-testid={`${testId}-search`}
          className="h-8 w-full rounded-md bg-slate-900/60 border border-slate-700 text-slate-100 text-sm pl-7 pr-2 focus:outline-none focus:ring-1 focus:ring-amber-500/40" />
      </div>
      <div className="max-h-40 overflow-y-auto rounded-md border border-slate-700 bg-slate-900/60 p-2 space-y-1">
        {names.length === 0 ? (
          <div className="text-xs text-slate-500 px-1 py-1">{emptyLabel}</div>
        ) : filtered.length === 0 ? (
          <div className="text-xs text-slate-500 px-1 py-1">Nessun risultato per “{q}”.</div>
        ) : filtered.map((n) => (
          <label key={n} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer px-1 py-0.5 rounded hover:bg-slate-800/60">
            <input type="checkbox" checked={(value || []).includes(n)} onChange={() => toggle(n)} data-testid={`${testId}-opt`} className="accent-amber-500" />
            {n}
          </label>
        ))}
      </div>
    </div>
  );
}
